import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { TimezoneTooltip } from './TimezoneTooltip';
import { cn } from '@/lib/utils';
import { ComponentProps } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface DateTimeInputProps extends Omit<ComponentProps<typeof Input>, 'type'> {
  /**
   * Label for the input
   */
  label: string;
  /**
   * Input ID (also used for label htmlFor)
   */
  id: string;
  /**
   * Type of date input: 'date' (no UTC label) or 'datetime-local' (with UTC label)
   */
  type?: 'date' | 'datetime-local';
  /**
   * Current value (used for timezone tooltip)
   * If not provided, tooltip will only show when input has a value
   */
  value?: string;
  /**
   * Optional container className
   */
  containerClassName?: string;
  /**
   * Whether this field is required
   */
  required?: boolean;
  /**
   * Optional timezone suffix to display (e.g., "(UTC)", "(EST)", "(JST)")
   * Only shown for datetime-local inputs
   * Defaults to "(UTC)" if not provided
   */
  timezoneSuffix?: string;
  /**
   * Optional description shown in an info tooltip next to the label
   */
  description?: string;
}

/**
 * DateTimeInput wraps a date or datetime-local input with:
 * - A label with optional UTC indicator (for datetime-local only)
 * - Timezone conversion tooltip (when value is present)
 * - Consistent styling
 */
export function DateTimeInput({
  label,
  id,
  type = 'datetime-local',
  value,
  containerClassName,
  required = false,
  timezoneSuffix = '(UTC)',
  className,
  description,
  ...inputProps
}: DateTimeInputProps) {
  const isDateTime = type === 'datetime-local';
  const hasValue = value && value.trim() !== '';

  return (
    <div className={cn('space-y-2', containerClassName)}>
      <div className="flex items-center gap-1">
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {isDateTime && (
            <span className="text-muted-foreground text-xs ml-1">{timezoneSuffix}</span>
          )}
        </Label>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger type="button">
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>{description}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {hasValue && <TimezoneTooltip utcDate={value} dateOnly={!isDateTime} />}
      </div>
      <Input type={type} id={id} value={value} className={className} {...inputProps} />
    </div>
  );
}
